import {
  genericGetRequestToApi,
  genericPostRequestToApi,
  genericPutRequestToApi,
  genericDeleteRequestToApi
} from '../utils/genericRequests'
import {
  CreateRetrieveAutoSearchUrl,
  UpdateDeleteAutoSearchUrl
} from '../utils/agentUrls'
import { isEmpty } from '../utils/generalUtils'
import {
  ADDRESS_TEXT_FIELDS,
  MLS_CITIES,
  STREET_SUFFIXES,
  STREET_FULL_SUFFIXES,
  STREET_DIRECTIONS
} from '../components/common/searchPropertyConst'

/**
 * Get all saved auto searches
 * @param {String} profileId - profile id
 */
const getSavedAutoSearchData = profileId =>
  genericGetRequestToApi(CreateRetrieveAutoSearchUrl(profileId))

/**
 * Creates new auto search based on search query send by agent
 * @param {String} clientId - client id
 * @param {Object} data - query data for search
 */
const createNewAutoSearch = (clientId, data) =>
  genericPostRequestToApi(CreateRetrieveAutoSearchUrl(clientId), data)

/**
 * Updates an auto search data
 * @param {String} clientId - client id
 * @param {String} autoSearchId - id of auto search element to be updated
 * @param {Object} updatedData - updated data object
 */
const updateAutoSearchData = (clientId, autoSearchId, updatedData) =>
  genericPutRequestToApi(
    UpdateDeleteAutoSearchUrl(clientId, autoSearchId),
    updatedData
  )

/**
 * Deletes an auto search element
 * @param {String} clientId - client id
 * @param {String} autoSearchId - id of auto search element to be deleted
 */
const deleteAutoSearchData = (clientId, autoSearchId) =>
  genericDeleteRequestToApi(UpdateDeleteAutoSearchUrl(clientId, autoSearchId))

// function to get custom price display text
const generateCustomPriceText = (value, max, searchType) => {
  if (!max) {
    max = searchType === 'rent' ? '5000' : '10000000'
  }
  const minPrice = value.substr(0, value.indexOf('-'))
  const maxPrice = value.substr(value.indexOf('-') + 1)
  let customPriceText = 'Any Price'
  if (minPrice !== '0') {
    customPriceText = `$${displayMetricValue(minPrice)}${
      parseInt(maxPrice) < parseInt(max)
        ? ' - $' + displayMetricValue(maxPrice)
        : '+'
    }`
  } else if (maxPrice !== max) {
    customPriceText = `$${displayMetricValue(minPrice)} - $${displayMetricValue(
      maxPrice
    )}`
  }
  return { minPrice, maxPrice, customPriceText }
}

// function to display numerical metric letters to numbers
const displayMetricValue = value => {
  const numberValue = parseInt(value)
  if (numberValue >= 1000000) {
    return (numberValue / 1000000).toString() + 'M'
  } else if (numberValue >= 1000) {
    return (numberValue / 1000).toString() + 'K'
  }
  return numberValue
}

const customAddressTextGenerator = queryObject => {
  let inputText = ''
  ADDRESS_TEXT_FIELDS.forEach(value => {
    inputText += getTextValue(queryObject[value.key], value.text)
  })
  return inputText
}

const getTextValue = (field, text) => {
  let inputText = ''
  if (!isEmpty(field)) {
    inputText = `${text}: `
    field.forEach(item => {
      inputText += `${item}, `
    })
    inputText = inputText.trim().slice(0, -1) + ' / '
  }
  return inputText
}

const getFieldName = field => {
  return field.replace(/([A-Z])/g, ' $1')
}

const getCustomFieldValue = (field, value, searchType) => {
  switch (field) {
    case 'Beds':
    case 'Baths':
      return `${value}+`
    case 'Price':
      return generateCustomPriceText(value, '', searchType).customPriceText
    case 'PropertySubType':
      return value.replace(/,/g, ', ')
    default:
      return value
  }
}

const addressInputPatternMatch = (value, query) => {
  // remove the state or provinde
  let newQuery = { ...query }
  value = value.replace(/,/g, '').replace(/usa/gi, '')
  value = value.replace(/tx/gi, '').replace(/texas/gi, '').trim()
  if (value.match(/^\d/)) {
    newQuery.StreetNumber.push(value.substr(0, value.indexOf(' ')))
    value = value.substr(value.indexOf(' ') + 1)
  }
  value = value.replace(/[0-9]/g, '')
  value = value.replace(/#(.*?)[\s]|#(.*)/g, '')
  value = value.replace(/\b[news]\b/gi, '')
  let emptyObject = true

  for (let i = 0; i < MLS_CITIES.length; i++) {
    const city = MLS_CITIES[i].toUpperCase()
    const regex = new RegExp('\\b' + city + '\\b', 'gi')
    if (value.toUpperCase().match(regex)) {
      value = value.replace(regex, '')
      newQuery.City.push(MLS_CITIES[i])
      emptyObject = false
      break
    }
  }

  let suffixFound = false
  for (let i = 0; i < STREET_SUFFIXES.length; i++) {
    const suffix = STREET_SUFFIXES[i].toUpperCase()
    const regex = new RegExp('\\b' + suffix + '\\b', 'gi')
    if (value.toUpperCase().match(regex)) {
      value = value.replace(regex, '')
      emptyObject = false
      suffixFound = true
      // newQuery.StreetSuffix.push(STREET_SUFFIXES[i])
      break
    }
  }
  if (!suffixFound) {
    for (let i = 0; i < STREET_FULL_SUFFIXES.length; i++) {
      const suffix = STREET_FULL_SUFFIXES[i].toUpperCase()
      const regex = new RegExp('\\b' + suffix + '\\b', 'gi')
      if (value.toUpperCase().match(regex)) {
        value = value.replace(regex, '')
        emptyObject = false
        // newQuery.StreetSuffix.push(STREET_SUFFIXES[i])
        break
      }
    }
  }

  for (let i = 0; i < STREET_DIRECTIONS.length; i++) {
    const dir = STREET_DIRECTIONS[i].toUpperCase()
    const regex = new RegExp('\\b' + dir + '\\b', 'gi')
    if (value.toUpperCase().match(regex)) {
      const tempValue = value.replace(regex, '')
      if (!isEmpty(tempValue)) value = tempValue
      break
    }
  }

  if (!emptyObject) {
    value = value.replace(',', '').trim()
    if (!isEmpty(value)) {
      newQuery.StreetName.push(value)
    }
  } else {
    if (!isEmpty(value)) {
      newQuery.Keywords = !isEmpty(query.Keywords)
        ? query.Keywords + '; ' + value
        : value
    }
  }
  return newQuery
}

export {
  getSavedAutoSearchData,
  createNewAutoSearch,
  updateAutoSearchData,
  deleteAutoSearchData,
  generateCustomPriceText,
  customAddressTextGenerator,
  getFieldName,
  getCustomFieldValue,
  addressInputPatternMatch
}
